import { useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import UndoIcon from '@mui/icons-material/Undo';
import AddIcon from '@mui/icons-material/Add';
import TuneIcon from '@mui/icons-material/Tune';
import LockIcon from '@mui/icons-material/Lock';
import BlockIcon from '@mui/icons-material/Block';
import ReceiptIcon from '@mui/icons-material/Receipt';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import { DataGrid, type GridColDef } from '@mui/x-data-grid';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import dayjs from 'dayjs';
import { PageHeader } from '../../../shared/components/PageHeader';
import { ErrorState } from '../../../shared/components/ErrorState';
import { describeApiError } from '../../../shared/api/errors';
import { purchaseRequestsHooks, useRcMovimentos } from '../hooks';
import { EstornarMovimentoDialog } from '../EstornarMovimentoDialog';
import { MovimentoLancamentoDialog, type MovimentoModo } from '../MovimentoLancamentoDialog';
import { EncerrarCancelarDialog, type EncerrarCancelarModo } from '../EncerrarCancelarDialog';
import { EmitirNotaFiscalDialog } from '../EmitirNotaFiscalDialog';
import { RcProjetosDialog } from '../RcProjetosDialog';
import type { RcMovimentoItem } from '../types';

function formatMoney(raw: number | string | null | undefined): string {
  if (raw === null || raw === undefined || raw === '') return '—';
  const n = Number(String(raw).replace(',', '.'));
  if (!Number.isFinite(n)) return String(raw);
  return n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

/** Só Ajuste Manual e Complemento não estornados podem ser estornados. */
function podeEstornar(m: RcMovimentoItem): boolean {
  return !m.estornado && (m.categoria === 'MAN' || m.categoria === 'COM');
}

export function PurchaseRequestDetailPage(): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const params = useParams<{ id: string }>();
  const id = params.id && Number.isFinite(Number(params.id)) ? Number(params.id) : null;

  const [estornarFor, setEstornarFor] = useState<RcMovimentoItem | null>(null);
  const [lancarModo, setLancarModo] = useState<MovimentoModo | null>(null);
  const [encerrarModo, setEncerrarModo] = useState<EncerrarCancelarModo | null>(null);
  const [emitirNfOpen, setEmitirNfOpen] = useState(false);
  const [projetosOpen, setProjetosOpen] = useState(false);

  const detailQuery = purchaseRequestsHooks.useDetail(id);
  const movimentosQuery = useRcMovimentos(id);
  const rc = detailQuery.data;
  const rcAtiva = rc?.status === 'ATV';
  const podeEmitirNf = rcAtiva && rc?.possuiNotaMae === false;

  const columns: GridColDef<RcMovimentoItem>[] = [
    {
      field: 'actions',
      headerName: '',
      width: 60,
      sortable: false,
      filterable: false,
      align: 'left',
      headerAlign: 'left',
      renderCell: (params) =>
        podeEstornar(params.row) ? (
          <Tooltip title={t('features.requisicoesCompra.extrato.estorno.action')}>
            <IconButton
              size="small"
              color="warning"
              aria-label={t('features.requisicoesCompra.extrato.estorno.actionAria', {
                categoria: params.row.categoriaLabel,
              })}
              onClick={() => setEstornarFor(params.row)}
            >
              <UndoIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        ) : null,
    },
    {
      field: 'dataHora',
      headerName: t('features.requisicoesCompra.extrato.columns.data'),
      width: 150,
      valueFormatter: (v: string) => (v ? dayjs(v).format('DD/MM/YYYY HH:mm') : '—'),
    },
    {
      field: 'categoria',
      headerName: t('features.requisicoesCompra.extrato.columns.categoria'),
      width: 160,
      renderCell: (params) => (
        <Chip size="small" variant="outlined" label={params.row.categoriaLabel || params.value} />
      ),
    },
    {
      field: 'valorEfetivo',
      headerName: t('features.requisicoesCompra.extrato.columns.valor'),
      width: 150,
      align: 'right',
      headerAlign: 'right',
      renderCell: (params) => {
        const credito = params.row.tipo === '+';
        return (
          <Typography
            variant="body2"
            color={credito ? 'success.main' : 'error.main'}
            fontWeight={600}
          >
            {credito ? '+' : '−'} {formatMoney(params.row.valor)}
          </Typography>
        );
      },
    },
    {
      field: 'saldoAcumulado',
      headerName: t('features.requisicoesCompra.extrato.columns.saldoAcumulado'),
      width: 150,
      align: 'right',
      headerAlign: 'right',
      valueFormatter: (v: string) => formatMoney(v),
    },
    {
      field: 'respCadastroNome',
      headerName: t('features.requisicoesCompra.extrato.columns.responsavel'),
      width: 180,
      valueFormatter: (v: string | null) => v ?? '—',
    },
    {
      field: 'observacao',
      headerName: t('features.requisicoesCompra.extrato.columns.observacao'),
      flex: 1,
      minWidth: 200,
      sortable: false,
      renderCell: (params) => (
        <Stack direction="row" spacing={0.5} alignItems="center">
          {params.row.estornado && (
            <Chip
              size="small"
              color="default"
              label={t('features.requisicoesCompra.extrato.estornadoBadge')}
            />
          )}
          <Typography variant="body2" noWrap>
            {params.row.observacao ?? '—'}
          </Typography>
        </Stack>
      ),
    },
  ];

  if (!id) {
    return (
      <Box>
        <PageHeader title={t('features.requisicoesCompra.extrato.title')} />
        <ErrorState description={t('features.requisicoesCompra.extrato.idInvalido')} />
      </Box>
    );
  }

  if (detailQuery.isLoadingError) {
    return (
      <Box>
        <PageHeader title={t('features.requisicoesCompra.extrato.title')} />
        <ErrorState
          description={describeApiError(detailQuery.error)}
          onRetry={() => detailQuery.refetch()}
        />
      </Box>
    );
  }

  const statusChip = rc ? (
    <Chip
      size="small"
      color={rc.status === 'ATV' ? 'success' : 'default'}
      variant={rc.status === 'CAN' ? 'outlined' : 'filled'}
      label={t(`features.requisicoesCompra.statusLabel.${rc.status}`, { defaultValue: rc.status })}
    />
  ) : null;

  return (
    <Box>
      <PageHeader
        title={rc ? rc.referencia : t('features.requisicoesCompra.extrato.title')}
        subtitle={t('features.requisicoesCompra.extrato.subtitle')}
        breadcrumbs={[
          { label: t('menu.clients') },
          { label: t('menu.clientsPurchaseRequests') },
          { label: rc?.referencia ?? '…' },
        ]}
      />

      <Stack spacing={2}>
        <Stack
          direction="row"
          spacing={1}
          justifyContent="space-between"
          flexWrap="wrap"
          useFlexGap
        >
          <Button
            size="small"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/clientes/requisicoes-compra')}
          >
            {t('features.requisicoesCompra.extrato.voltar')}
          </Button>
          <Button
            size="small"
            variant="outlined"
            startIcon={<AccountTreeIcon />}
            onClick={() => setProjetosOpen(true)}
          >
            {t('features.requisicoesCompra.projetos.action')}
          </Button>
        </Stack>

        <Paper variant="outlined" sx={{ p: 2 }}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={3}
            divider={<Box sx={{ borderLeft: 1, borderColor: 'divider' }} />}
          >
            <Field
              label={t('features.requisicoesCompra.fields.cliente')}
              value={rc?.clienteNome ?? '—'}
            />
            <Field
              label={t('features.requisicoesCompra.columns.valorInicial')}
              value={formatMoney(rc?.valorInicial)}
            />
            <Field
              label={t('features.requisicoesCompra.columns.saldo')}
              value={formatMoney(rc?.saldo)}
              strong
            />
            <Box>
              <Typography variant="caption" color="text.secondary" display="block">
                {t('features.requisicoesCompra.columns.status')}
              </Typography>
              <Box sx={{ mt: 0.5 }}>{statusChip}</Box>
            </Box>
          </Stack>
        </Paper>

        {rcAtiva && (
          <Stack
            direction="row"
            spacing={1}
            flexWrap="wrap"
            useFlexGap
            justifyContent="space-between"
          >
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setLancarModo('complementar')}
              >
                {t('features.requisicoesCompra.movimentos.complementar.action')}
              </Button>
              <Button
                variant="outlined"
                startIcon={<TuneIcon />}
                onClick={() => setLancarModo('ajuste-manual')}
              >
                {t('features.requisicoesCompra.movimentos.ajuste.action')}
              </Button>
              {podeEmitirNf && (
                <Button
                  variant="outlined"
                  color="secondary"
                  startIcon={<ReceiptIcon />}
                  onClick={() => setEmitirNfOpen(true)}
                >
                  {t('features.requisicoesCompra.emitirNf.action')}
                </Button>
              )}
            </Stack>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Button
                variant="outlined"
                color="error"
                startIcon={<LockIcon />}
                onClick={() => setEncerrarModo('encerrar')}
              >
                {t('features.requisicoesCompra.encerrarCancelar.encerrar.action')}
              </Button>
              <Button
                variant="outlined"
                color="error"
                startIcon={<BlockIcon />}
                onClick={() => setEncerrarModo('cancelar')}
              >
                {t('features.requisicoesCompra.encerrarCancelar.cancelar.action')}
              </Button>
            </Stack>
          </Stack>
        )}

        <Box>
          <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
            {t('features.requisicoesCompra.extrato.movimentosTitle')}
          </Typography>
          {movimentosQuery.isLoadingError ? (
            <ErrorState
              description={describeApiError(movimentosQuery.error)}
              onRetry={() => movimentosQuery.refetch()}
            />
          ) : (
            <DataGrid<RcMovimentoItem>
              rows={movimentosQuery.data ?? []}
              columns={columns}
              getRowId={(r) => r.id}
              loading={movimentosQuery.isLoading || movimentosQuery.isFetching}
              autoHeight
              disableRowSelectionOnClick
              disableColumnMenu
              disableColumnSorting
              disableVirtualization
              hideFooter
              localeText={{
                noRowsLabel: t('features.requisicoesCompra.extrato.vazio'),
              }}
              sx={{ minHeight: 200 }}
              slots={{
                loadingOverlay: () => (
                  <Box display="flex" justifyContent="center" py={4}>
                    <CircularProgress />
                  </Box>
                ),
              }}
            />
          )}
        </Box>
      </Stack>

      <EstornarMovimentoDialog
        open={estornarFor !== null}
        purchaseRequestId={id}
        movimento={estornarFor}
        onClose={() => setEstornarFor(null)}
      />

      {lancarModo && (
        <MovimentoLancamentoDialog
          open
          purchaseRequestId={id}
          modo={lancarModo}
          onClose={() => setLancarModo(null)}
        />
      )}

      {encerrarModo && (
        <EncerrarCancelarDialog
          open
          purchaseRequestId={id}
          modo={encerrarModo}
          saldo={String(rc?.saldo ?? 0)}
          onClose={() => setEncerrarModo(null)}
        />
      )}

      {emitirNfOpen && (
        <EmitirNotaFiscalDialog
          open
          purchaseRequestId={id}
          saldo={String(rc?.saldo ?? 0)}
          onClose={() => setEmitirNfOpen(false)}
        />
      )}

      {projetosOpen && (
        <RcProjetosDialog
          open
          purchaseRequestId={id}
          clienteId={rc?.clienteId ?? null}
          onClose={() => setProjetosOpen(false)}
        />
      )}
    </Box>
  );
}

function Field({
  label,
  value,
  strong,
}: {
  label: string;
  value: string;
  strong?: boolean;
}): JSX.Element {
  return (
    <Box>
      <Typography variant="caption" color="text.secondary" display="block">
        {label}
      </Typography>
      <Typography variant={strong ? 'h6' : 'body1'} fontWeight={strong ? 700 : 400}>
        {value}
      </Typography>
    </Box>
  );
}
